import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Copy, ExternalLink, RefreshCw, Users, Mail, Phone, Clapperboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:  { color: '#f59e0b', icon: Clock,        label: 'Pending' },
  approved: { color: '#34d399', icon: CheckCircle,   label: 'Approved' },
  rejected: { color: '#f87171', icon: XCircle,       label: 'Rejected' },
};

export default function AdminBetaPage() {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState({});
  const [inviteLinks, setInviteLinks] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const q = filter !== 'all' ? `?status=${filter}` : '';
      const { data } = await axios.get(`${API}/admin/beta-requests${q}`, { headers });
      setRequests(data);
    } catch {
      toast.error('Failed to load beta requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (req) => {
    setActionLoading(p => ({ ...p, [req.id]: 'approving' }));
    try {
      const { data } = await axios.patch(`${API}/admin/beta-requests/${req.id}`,
        { status: 'approved' }, { headers });
      if (data.invite_link) {
        setInviteLinks(p => ({ ...p, [req.id]: data.invite_link }));
        toast.success(`Approved! Invite link generated for ${req.email}`);
      }
      await load();
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(p => ({ ...p, [req.id]: null }));
    }
  };

  const reject = async (req) => {
    setActionLoading(p => ({ ...p, [req.id]: 'rejecting' }));
    try {
      await axios.patch(`${API}/admin/beta-requests/${req.id}`,
        { status: 'rejected' }, { headers });
      toast.success('Request rejected');
      await load();
    } catch {
      toast.error('Failed to reject request');
    } finally {
      setActionLoading(p => ({ ...p, [req.id]: null }));
    }
  };

  const buildInviteLink = (req) => {
    const base = process.env.REACT_APP_BACKEND_URL.replace('/api', '').replace('https://', '').split('/')[0];
    return `https://${base}/register?invite=${req.invite_token}&email=${encodeURIComponent(req.email)}`;
  };

  const copyLink = (req) => {
    const link = inviteLinks[req.id] || (req.invite_token ? buildInviteLink(req) : null);
    if (!link) return;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  const filtered = filter === 'all' ? requests : requests.filter(r => r.status === filter);

  return (
    <div className="min-h-screen bg-[#0d0d0d] p-6" data-testid="admin-beta-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-[#f0f0f0]">Beta Access Requests</h1>
            <p className="text-sm text-[#a0a0a0] mt-0.5">Review and approve beta programme applications</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-[#2a2a2a] text-[#a0a0a0]">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { key: 'all',      label: 'Total',    color: '#a0a0a0' },
            { key: 'pending',  label: 'Pending',  color: '#f59e0b' },
            { key: 'approved', label: 'Approved', color: '#34d399' },
            { key: 'rejected', label: 'Rejected', color: '#f87171' },
          ].map(s => (
            <button key={s.key}
                    onClick={() => setFilter(s.key)}
                    data-testid={`beta-filter-${s.key}`}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      filter === s.key ? 'border-opacity-60' : 'border-[#1a1a1a] hover:border-[#2a2a2a]'
                    }`}
                    style={{ background: '#141414', borderColor: filter === s.key ? s.color + '60' : undefined }}>
              <div className="text-2xl font-bold mb-0.5" style={{ color: s.color }}>{counts[s.key]}</div>
              <div className="text-xs text-[#a0a0a0]">{s.label}</div>
            </button>
          ))}
        </div>

        {/* Request list */}
        {loading ? (
          <div className="text-center py-16 text-[#a0a0a0]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-10 w-10 text-[#3a3a3a] mx-auto mb-3" />
            <p className="text-[#a0a0a0]">No {filter !== 'all' ? filter : ''} requests yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(req => {
              const meta = STATUS_META[req.status] || STATUS_META.pending;
              const StatusIcon = meta.icon;
              const isActing = actionLoading[req.id];
              const inviteLink = inviteLinks[req.id] || (req.invite_token ? buildInviteLink(req) : null);

              return (
                <Card key={req.id} className="bg-[#141414] border-[#1e1e1e] p-5"
                      data-testid={`beta-request-${req.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    {/* Left: applicant info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-[#f0f0f0] text-sm">{req.name}</span>
                        <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ background: meta.color + '14', color: meta.color }}>
                          <StatusIcon className="h-3 w-3" />
                          {meta.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-[#a0a0a0]">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{req.email}</span>
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{req.phone}</span>
                        <span className="flex items-center gap-1"><Clapperboard className="h-3 w-3" />{req.project}</span>
                        <span className="text-[#808080]">{req.production_house_or_director}</span>
                      </div>
                      {req.notes && (
                        <p className="mt-2 text-xs text-[#707070] italic">"{req.notes}"</p>
                      )}
                      <p className="mt-1.5 text-[10px] text-[#505050]">
                        Applied {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Right: actions */}
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {req.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => approve(req)} disabled={!!isActing}
                                  className="bg-[#34d399]/10 border border-[#34d399]/30 text-[#34d399] hover:bg-[#34d399]/20 h-8 text-xs"
                                  data-testid={`approve-btn-${req.id}`}>
                            {isActing === 'approving' ? 'Approving…' : <><CheckCircle className="h-3.5 w-3.5 mr-1" />Approve</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => reject(req)} disabled={!!isActing}
                                  className="border-[#f87171]/30 text-[#f87171] hover:bg-[#f87171]/10 h-8 text-xs"
                                  data-testid={`reject-btn-${req.id}`}>
                            {isActing === 'rejecting' ? 'Rejecting…' : <><XCircle className="h-3.5 w-3.5 mr-1" />Reject</>}
                          </Button>
                        </div>
                      )}

                      {/* Invite link for approved */}
                      {req.status === 'approved' && inviteLink && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => copyLink(req)}
                                  className="bg-[#e8c547]/10 border border-[#e8c547]/30 text-[#e8c547] hover:bg-[#e8c547]/20 h-8 text-xs"
                                  data-testid={`copy-link-${req.id}`}>
                            <Copy className="h-3.5 w-3.5 mr-1" />Copy Invite Link
                          </Button>
                          <Button size="sm" variant="ghost"
                                  onClick={() => window.open(inviteLink, '_blank')}
                                  className="text-[#a0a0a0] h-8 text-xs">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}

                      {req.status === 'approved' && req.invite_used && (
                        <span className="text-[10px] text-[#34d399]">✓ Account created</span>
                      )}
                      {req.status === 'approved' && !req.invite_used && inviteLink && (
                        <span className="text-[10px] text-[#a0a0a0]">Invite not yet used</span>
                      )}

                      {req.status === 'rejected' && (
                        <Button size="sm" variant="outline" onClick={() => approve(req)} disabled={!!isActing}
                                className="border-[#2a2a2a] text-[#a0a0a0] h-8 text-xs">
                          Approve Instead
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Invite link display (full, for easy copying) */}
                  {req.status === 'approved' && inviteLink && (
                    <div className="mt-3 p-2.5 rounded-lg bg-[#0d0d0d] border border-[#2a2a2a]"
                         onClick={() => copyLink(req)}
                         style={{ cursor: 'pointer' }}
                         title="Click to copy">
                      <p className="text-[10px] text-[#505050] mb-0.5">Invite link (click to copy):</p>
                      <p className="text-[11px] text-[#e8c547] font-mono break-all">{inviteLink}</p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
